import React from 'react';
import './Textarea.css';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  id?: string;
  name?: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  variant?: 'default' | 'modal';
  label?: string;
  hint?: string;
  labelClassName?: string;
  hintClassName?: string;
}

/**
 * Reusable Textarea component with label and hint support.
 *
 * Props are passed through transparently (like TextInput) so the field can be
 * used as either a controlled input (caller passes value/onChange) or an
 * uncontrolled input (caller passes ref/defaultValue, e.g. via
 * react-hook-form's register()). Forcing `value={value || ''}` would lock the
 * field to an empty string whenever no value prop is provided, which prevents
 * typing and pasting.
 */
const Textarea: React.FC<TextareaProps> = ({
  id,
  placeholder,
  rows = 4,
  disabled = false,
  required = false,
  className = '',
  variant = 'default',
  label,
  hint,
  labelClassName = '',
  hintClassName = '',
  ...rest
}) => {
  const baseClass = `common-textarea common-textarea--${variant}`;
  const textareaClassName = className ? `${baseClass} ${className}` : baseClass;

  const labelClass = variant === 'modal'
    ? `common-textarea__label ${labelClassName}`.trim()
    : `common-textarea__label common-textarea__label--default ${labelClassName}`.trim();

  const hintClass = variant === 'modal'
    ? `common-textarea__hint ${hintClassName}`.trim()
    : `common-textarea__hint common-textarea__hint--default ${hintClassName}`.trim();

  const textareaElement = (
    <textarea
      id={id}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      rows={rows}
      className={textareaClassName}
      {...rest}
    />
  );

  // If label or hint is provided, wrap in a container
  if (label || hint) {
    return (
      <div className="common-textarea__wrapper">
        {label && (
          <label htmlFor={id} className={labelClass}>
            {label}
          </label>
        )}
        {textareaElement}
        {hint && (
          <small className={hintClass}>
            {hint}
          </small>
        )}
      </div>
    );
  }

  // Otherwise, return just the textarea element
  return textareaElement;
};

export default Textarea;

